import { useT } from "@transifex/react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import IconButton from "../iconButton/IconButton";
import ToastMessage from "../toastMessage/ToastMessage";
import { ButtonColorVariant } from "../../complex/teacherArea/AreaButton";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { ShareModes } from "../../enums/shareFlow";
import { getClassroomUrl } from "../../helpers/schoolClassHelpers";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import useToast from "../../hooks/useToast";
import BasePopupWrapper, { TitleColorVariants } from "./BasePopupWrapper";

export enum ShareActivityTabs {
    EXERCISE = "EXERCISE",
    CLASS = "CLASS",
}

interface ShareActivityLinkPopupProps {
    isOpen: boolean;
    onClose: () => void;
    activityLink: string;
    schoolClassId?: string;
    category: ClassActivityCategory;
    shareMode?: ShareModes | null;
    // Receives the active tab: the two tabs copy different links (activity vs classroom), so the
    // caller cannot assume which one was copied.
    onLinkCopied?: (linkTarget: ShareActivityTabs) => void;
}

const categoryButtonColor: Record<ClassActivityCategory, ButtonColorVariant> = {
    [ClassActivityCategory.TEST]: ButtonColorVariant.DEFAULT,
    [ClassActivityCategory.VOCAB_TRAINING]: ButtonColorVariant.SECONDARY,
};

const categoryTitleColor: Record<ClassActivityCategory, TitleColorVariants> = {
    [ClassActivityCategory.TEST]: TitleColorVariants.DEFAULT,
    [ClassActivityCategory.VOCAB_TRAINING]: TitleColorVariants.SECONDARY,
};

const categoryQrBorder: Record<ClassActivityCategory, string> = {
    [ClassActivityCategory.TEST]: "border-p6-orange-brighter",
    [ClassActivityCategory.VOCAB_TRAINING]: "border-forest-green-800",
};

const ShareActivityLinkPopup = (props: ShareActivityLinkPopupProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_shareTitle = t("Share Link", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_tabLinkTo = t("Link to", { _tags: "ClassActivity, ShareLinkPopup" });
    // Custom key: the keyless "Test" string translates to "Aufgabe" (phase6's feature name), but
    // share-mode labels must read "Test" in German too. The key must be an inline literal (the
    // Transifex CLI extractor can't resolve constants) and identical at every share-mode label.
    const t_tabTest = t("Test", { _key: "test.mode.label", _tags: "ClassActivity, ShareLinkPopup" });
    const t_tabExercise = t("Exercise", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_tabVocabTraining = t("Vocab Training", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_tabClass = t("Class", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_titleTest = t("Share link for test", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_titleExercise = t("Share link for exercise", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_titleVocabTraining = t("Share link for vocab training", {
        _tags: "ClassActivity, ShareLinkPopup",
    });
    const t_scanQr = t("Scan code", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_scanQrClass = t("Scan the code and log in", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_close = t("Close", { _tags: "ClassActivity, ShareLinkPopup" });
    const t_linkCopied = t("Link copied – you can paste it with Ctrl+V/Cmd+V.", {
        _tags: "ClassActivity, ShareLinkPopup",
    });
    const t_sr_copyLink = t("Copy link", { _tags: "ClassActivity, ShareLinkPopup, ScreenReaderOnly" });
    const t_sr_qrCode = t("QR Code", { _tags: "ClassActivity, ShareLinkPopup, ScreenReaderOnly" });

    const [activeTab, setActiveTab] = useState<ShareActivityTabs>(ShareActivityTabs.EXERCISE);
    const [qrDataUrl, setQrDataUrl] = useState("");
    const [qrFailed, setQrFailed] = useState(false);
    const linkCopiedToast = useToast();

    const hasClassTab = !!props.schoolClassId;
    const classUrl = props.schoolClassId ? getClassroomUrl(props.schoolClassId) : "";
    const currentUrl = activeTab === ShareActivityTabs.CLASS ? classUrl : props.activityLink;

    const isVocabTraining = props.category === ClassActivityCategory.VOCAB_TRAINING;
    const isPracticeMode = props.shareMode === ShareModes.PRACTICE;

    const getActivityTabLabel = (): string => {
        if (isVocabTraining) return t_tabVocabTraining;
        if (isPracticeMode) return t_tabExercise;
        return t_tabTest;
    };
    const getLegacyTitle = (): string => {
        if (isVocabTraining) return t_titleVocabTraining;
        if (isPracticeMode) return t_titleExercise;
        return t_titleTest;
    };
    const activityTabLabel = getActivityTabLabel();
    const popupTitle = hasClassTab ? t_shareTitle : getLegacyTitle();

    useEffect(() => {
        if (!props.isOpen || !hasClassTab) {
            setActiveTab(ShareActivityTabs.EXERCISE);
        }
    }, [props.isOpen, hasClassTab]);

    useEffect(() => {
        setQrDataUrl("");
        setQrFailed(false);

        if (!props.isOpen || !currentUrl) return;

        let cancelled = false;
        QRCode.toDataURL(currentUrl, { width: 190, margin: 1 })
            .then((url: string) => {
                if (!cancelled) setQrDataUrl(url);
            })
            .catch(() => {
                if (!cancelled) setQrFailed(true);
            });

        return () => {
            cancelled = true;
        };
    }, [props.isOpen, currentUrl]);

    const handleCopyLink = () => {
        if (!currentUrl) return;
        navigator.clipboard
            .writeText(currentUrl)
            .then(() => {
                linkCopiedToast.triggerToast();
                props.onLinkCopied?.(activeTab);
            })
            .catch(() => {});
    };

    const renderTab = (tab: ShareActivityTabs, label: string) => {
        const isActive = activeTab === tab;
        const borderClass = isActive ? "border-2 border-p6-grey-dark" : "border border-p6-grey-light";
        return (
            <button
                type="button"
                onClick={() => setActiveTab(tab)}
                aria-pressed={isActive}
                className={`${borderClass} flex-1 flex flex-col items-center justify-center px-m py-xs rounded-small-container bg-white cursor-pointer overflow-clip min-h-[67px] w-full`}
            >
                <p className="text-l text-p6-grey-dark text-center">{t_tabLinkTo}</p>
                <p className="text-xl font-semibold text-p6-grey-dark text-center">{label}</p>
            </button>
        );
    };

    const renderTabs = () => {
        if (!hasClassTab) return null;
        return (
            <div className={`flex gap-s w-full ${getStyleBasedOnBreakpoint(Breakpoint.SM, "flex-row", "flex-col")}`}>
                {renderTab(ShareActivityTabs.EXERCISE, activityTabLabel)}
                {renderTab(ShareActivityTabs.CLASS, t_tabClass)}
            </div>
        );
    };

    const renderQrSection = () => {
        if (qrFailed) return null;
        const qrSubtitle = activeTab === ShareActivityTabs.CLASS ? t_scanQrClass : t_scanQr;
        const qrBorderClass = categoryQrBorder[props.category];
        return (
            <div
                className={`flex flex-col gap-s items-center justify-center border ${qrBorderClass} rounded-small-container p-m w-full overflow-clip`}
            >
                {qrDataUrl && (
                    <img
                        src={qrDataUrl}
                        alt={t_sr_qrCode}
                        className="w-[188px] max-w-full h-auto"
                    />
                )}
                <p className="text-l text-p6-grey-dark text-center">{qrSubtitle}</p>
            </div>
        );
    };

    const renderCopyRow = () => (
        <div className="flex gap-s items-center justify-center w-full px-xs">
            <p className="text-l text-p6-grey-dark flex-1 break-all">{currentUrl}</p>
            <IconButton
                iconName="duplicate"
                ariaLabel={t_sr_copyLink}
                onClick={handleCopyLink}
            />
        </div>
    );

    return (
        <>
            <BasePopupWrapper
                isOpen={props.isOpen}
                onClose={props.onClose}
                title={popupTitle}
                titleColor={categoryTitleColor[props.category]}
                maxWidth={448}
                primaryButton={{
                    text: t_close,
                    onClick: props.onClose,
                    colorVariant: categoryButtonColor[props.category],
                }}
            >
                <div className="flex flex-col gap-m items-center w-full">
                    {renderTabs()}
                    {renderQrSection()}
                    {renderCopyRow()}
                </div>
            </BasePopupWrapper>

            <ToastMessage
                isOpen={linkCopiedToast.isOpen}
                onOpenChange={linkCopiedToast.onOpenChange}
                text={t_linkCopied}
                duration={7000}
            />
        </>
    );
};

export default ShareActivityLinkPopup;
